/**
 * 플레이리스트 상세 페이지 모달 상태 관리 훅
 *
 * 9개의 모달 상태를 하나의 훅으로 통합 관리합니다.
 * - 플레이리스트 삭제/재생성/복제
 * - 트랙 추가/업로드/수정/삭제/재생성
 * - 비디오 생성
 */

'use client';

import { useReducer, useCallback } from 'react';
import type { TrackDetail } from '@/lib/api/orval/models';

// ============================================================================
// Types
// ============================================================================

export type ModalType =
  | 'deletePlaylist'
  | 'regeneratePlaylist'
  | 'addTracks'
  | 'uploadTrack'
  | 'generateVideo'
  | 'duplicatePlaylist'
  | 'editTrack'
  | 'editTrackPrompts'
  | 'deleteTrack'
  | 'regenerateTrack'
  | 'generateMetadata'
  | 'exportAudio'
  | 'exportYoutubeVideo'
  | 'generateMusicConfirm';

interface ModalState {
  deletePlaylist: boolean;
  regeneratePlaylist: boolean;
  addTracks: boolean;
  uploadTrack: boolean;
  generateVideo: boolean;
  duplicatePlaylist: boolean;
  editTrack: boolean;
  editTrackPrompts: boolean;
  deleteTrack: boolean;
  regenerateTrack: boolean;
  generateMetadata: boolean;
  exportAudio: boolean;
  exportYoutubeVideo: boolean;
  generateMusicConfirm: boolean;
  selectedTrack: TrackDetail | null;
  deleteTargetTracks: TrackDetail[];
  musicConfirmTrack: TrackDetail | null;
  trackCountInput: string;
  metadataTrackCountInput: string;
  editFormData: { title: string; lyrics: string };
}

type ModalAction =
  | { type: 'OPEN_MODAL'; modal: ModalType }
  | { type: 'CLOSE_MODAL'; modal: ModalType }
  | { type: 'CLOSE_ALL' }
  | { type: 'SELECT_TRACK'; track: TrackDetail | null }
  | { type: 'SET_TRACK_COUNT'; count: string }
  | { type: 'SET_METADATA_TRACK_COUNT'; count: string }
  | { type: 'SET_EDIT_FORM'; data: { title: string; lyrics: string } }
  | { type: 'OPEN_EDIT_TRACK'; track: TrackDetail }
  | { type: 'OPEN_EDIT_TRACK_PROMPTS'; track: TrackDetail }
  | { type: 'OPEN_DELETE_TRACK'; track: TrackDetail }
  | { type: 'OPEN_DELETE_TRACKS'; tracks: TrackDetail[] }
  | { type: 'OPEN_REGENERATE_TRACK'; track: TrackDetail }
  | { type: 'OPEN_GENERATE_MUSIC_CONFIRM'; track: TrackDetail };

// ============================================================================
// Initial State & Reducer
// ============================================================================

const initialState: ModalState = {
  deletePlaylist: false,
  regeneratePlaylist: false,
  addTracks: false,
  uploadTrack: false,
  generateVideo: false,
  duplicatePlaylist: false,
  editTrack: false,
  editTrackPrompts: false,
  deleteTrack: false,
  regenerateTrack: false,
  generateMetadata: false,
  exportAudio: false,
  exportYoutubeVideo: false,
  generateMusicConfirm: false,
  selectedTrack: null,
  deleteTargetTracks: [],
  musicConfirmTrack: null,
  trackCountInput: '1',
  metadataTrackCountInput: '5',
  editFormData: { title: '', lyrics: '' },
};

function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, [action.modal]: true };

    case 'CLOSE_MODAL':
      // 트랙 관련 모달을 닫을 때 selectedTrack도 초기화
      if (['editTrack', 'editTrackPrompts', 'deleteTrack', 'regenerateTrack'].includes(action.modal)) {
        return { ...state, [action.modal]: false, selectedTrack: null, deleteTargetTracks: [] };
      }
      // addTracks 모달 닫을 때 카운트 초기화
      if (action.modal === 'addTracks') {
        return { ...state, [action.modal]: false, trackCountInput: '1' };
      }
      // generateMetadata 모달 닫을 때 카운트 초기화
      if (action.modal === 'generateMetadata') {
        return { ...state, [action.modal]: false, metadataTrackCountInput: '5' };
      }
      // generateMusicConfirm 모달 닫을 때 musicConfirmTrack 초기화
      if (action.modal === 'generateMusicConfirm') {
        return { ...state, [action.modal]: false, musicConfirmTrack: null };
      }
      return { ...state, [action.modal]: false };

    case 'CLOSE_ALL':
      return initialState;

    case 'SELECT_TRACK':
      return { ...state, selectedTrack: action.track };

    case 'SET_TRACK_COUNT':
      return { ...state, trackCountInput: action.count };

    case 'SET_METADATA_TRACK_COUNT':
      return { ...state, metadataTrackCountInput: action.count };

    case 'SET_EDIT_FORM':
      return { ...state, editFormData: action.data };

    case 'OPEN_EDIT_TRACK':
      return {
        ...state,
        editTrack: true,
        selectedTrack: action.track,
        editFormData: {
          title: action.track.title || '',
          lyrics: action.track.lyrics || '',
        },
      };

    case 'OPEN_DELETE_TRACK':
      return {
        ...state,
        deleteTrack: true,
        selectedTrack: action.track,
        deleteTargetTracks: [action.track],
      };

    case 'OPEN_DELETE_TRACKS':
      return {
        ...state,
        deleteTrack: true,
        selectedTrack: action.tracks[0] || null,
        deleteTargetTracks: action.tracks,
      };

    case 'OPEN_REGENERATE_TRACK':
      return {
        ...state,
        regenerateTrack: true,
        selectedTrack: action.track,
      };

    case 'OPEN_EDIT_TRACK_PROMPTS':
      return {
        ...state,
        editTrackPrompts: true,
        selectedTrack: action.track,
      };

    case 'OPEN_GENERATE_MUSIC_CONFIRM':
      return {
        ...state,
        generateMusicConfirm: true,
        musicConfirmTrack: action.track,
      };

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function usePlaylistModals() {
  const [state, dispatch] = useReducer(modalReducer, initialState);

  // 모달 열기/닫기
  const openModal = useCallback((modal: ModalType) => {
    dispatch({ type: 'OPEN_MODAL', modal });
  }, []);

  const closeModal = useCallback((modal: ModalType) => {
    dispatch({ type: 'CLOSE_MODAL', modal });
  }, []);

  const closeAllModals = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL' });
  }, []);

  // 트랙 관련 모달 헬퍼
  const openEditTrack = useCallback((track: TrackDetail) => {
    dispatch({ type: 'OPEN_EDIT_TRACK', track });
  }, []);

  const openEditTrackPrompts = useCallback((track: TrackDetail) => {
    dispatch({ type: 'OPEN_EDIT_TRACK_PROMPTS', track });
  }, []);

  const openDeleteTrack = useCallback((track: TrackDetail) => {
    dispatch({ type: 'OPEN_DELETE_TRACK', track });
  }, []);

  const openDeleteTracks = useCallback((tracks: TrackDetail[]) => {
    if (tracks.length === 1) {
      dispatch({ type: 'OPEN_DELETE_TRACK', track: tracks[0] });
    } else if (tracks.length > 1) {
      dispatch({ type: 'OPEN_DELETE_TRACKS', tracks });
    }
  }, []);

  const openRegenerateTrack = useCallback((track: TrackDetail) => {
    dispatch({ type: 'OPEN_REGENERATE_TRACK', track });
  }, []);

  const openGenerateMusicConfirm = useCallback((track: TrackDetail) => {
    dispatch({ type: 'OPEN_GENERATE_MUSIC_CONFIRM', track });
  }, []);

  // 폼 데이터 관리
  const setTrackCount = useCallback((count: string) => {
    dispatch({ type: 'SET_TRACK_COUNT', count });
  }, []);

  const setEditFormData = useCallback((data: { title: string; lyrics: string }) => {
    dispatch({ type: 'SET_EDIT_FORM', data });
  }, []);

  const setMetadataTrackCount = useCallback((count: string) => {
    dispatch({ type: 'SET_METADATA_TRACK_COUNT', count });
  }, []);

  return {
    // 모달 상태
    isOpen: {
      deletePlaylist: state.deletePlaylist,
      regeneratePlaylist: state.regeneratePlaylist,
      addTracks: state.addTracks,
      uploadTrack: state.uploadTrack,
      generateVideo: state.generateVideo,
      duplicatePlaylist: state.duplicatePlaylist,
      editTrack: state.editTrack,
      editTrackPrompts: state.editTrackPrompts,
      deleteTrack: state.deleteTrack,
      regenerateTrack: state.regenerateTrack,
      generateMetadata: state.generateMetadata,
      exportAudio: state.exportAudio,
      exportYoutubeVideo: state.exportYoutubeVideo,
      generateMusicConfirm: state.generateMusicConfirm,
    },

    // 선택된 트랙
    selectedTrack: state.selectedTrack,
    deleteTargetTracks: state.deleteTargetTracks,
    musicConfirmTrack: state.musicConfirmTrack,

    // 폼 데이터
    trackCountInput: state.trackCountInput,
    metadataTrackCountInput: state.metadataTrackCountInput,
    editFormData: state.editFormData,

    // 액션
    openModal,
    closeModal,
    closeAllModals,
    openEditTrack,
    openEditTrackPrompts,
    openDeleteTrack,
    openDeleteTracks,
    openRegenerateTrack,
    openGenerateMusicConfirm,
    setTrackCount,
    setMetadataTrackCount,
    setEditFormData,
  };
}

export type UsePlaylistModalsReturn = ReturnType<typeof usePlaylistModals>;
